// liltoon 近似シェーダ（M3）。
// シェイプキー（モーフ）は Three.js 標準機構を使わず、2D デルタテクスチャを
// texelFetch で読む自前実装（MORPH_HEAD / MORPH_BODY）。256 制限の回避のため。
// メッシュ固有 uniform なので、マテリアルは viewer.ts でメッシュごとに clone して割り当てる。
//
// 前身プロジェクト（faceweb）からの改善点:
//  - 「ライティングが弱い」問題への対処として、環境光 + ディレクショナル 1 灯を
//    明示的な uniform（ambientColor / lightColor / lightDir）で持ち、UI から調整できる。
//  - トゥーンランプを shadeBorder / shadeBlur で制御（liltoon の shadowBorder/shadowBlur を直接使用）。
//  - 影側も真っ黒にならないよう albedo 側の色補間と光量を分けて合成する。

import * as THREE from "three";
import type { CullMode, FmMaterial } from "../core/types.ts";

/** アウトライン幅の描画時上限（メートル）。壊れた値で顔が覆われるのを防ぐ。 */
export const OUTLINE_MAX_METERS = 0.01;

/**
 * outlineWidth → メートル換算係数。
 * 仕様書では「単位メートル」と書かれているが、実データ（liltoon の `_OutlineWidth`）は
 * 0.05〜0.08 のような値で、そのままメートルとして扱うと 5〜8cm の巨大な殻になる。
 * liltoon 本体も `_OutlineWidth * 0.01` をワールド幅として使うため、同じ係数を掛ける。
 */
export const OUTLINE_WIDTH_SCALE = 0.01;

/** すべての liltoon マテリアルで共有するライティング uniform。 */
export interface LightingUniforms {
  lightDir: { value: THREE.Vector3 };
  lightColor: { value: THREE.Color };
  ambientColor: { value: THREE.Color };
}

export interface LightingSettings {
  /** 水平角（度）。0 = 正面から、+ で右へ回る */
  yaw: number;
  /** 仰角（度）。+ で上から */
  pitch: number;
  /** ディレクショナル光の強さ */
  intensity: number;
  /** 環境光の強さ */
  ambient: number;
}

export const DEFAULT_LIGHTING: LightingSettings = {
  yaw: -25,
  pitch: 28,
  // ambient + intensity ≒ 1.0 になるように既定値を選び、明部が白飛びしないようにする
  intensity: 0.32,
  ambient: 0.7,
};

export function createLightingUniforms(): LightingUniforms {
  const u: LightingUniforms = {
    lightDir: { value: new THREE.Vector3() },
    lightColor: { value: new THREE.Color() },
    ambientColor: { value: new THREE.Color() },
  };
  applyLighting(u, DEFAULT_LIGHTING);
  return u;
}

/** 設定値を uniform へ反映する（表面→光源のワールド方向ベクトルを作る）。 */
export function applyLighting(u: LightingUniforms, s: LightingSettings): void {
  const yaw = (s.yaw * Math.PI) / 180;
  const pitch = (s.pitch * Math.PI) / 180;
  // アバターは Z 反転後 -Z 方向を向く。yaw=0 で正面（-Z 側）から照らす。
  u.lightDir.value
    .set(Math.sin(yaw) * Math.cos(pitch), Math.sin(pitch), -Math.cos(yaw) * Math.cos(pitch))
    .normalize();
  // 暖色のキー / わずかに青い環境光（Linear 値としてそのまま使う）
  // factor=1（完全に光が当たる面）で ambient+light が 1.0 をわずかに超えると
  // 明部が白側へクリップし、彩度の高いテクスチャ（瞳など）ほど不自然に色褪せて見える。
  // 各チャンネルの最大値ぶんだけ均等スケールダウンして 1.0 に収める。
  const lr = s.intensity * 1.02;
  const lg = s.intensity;
  const lb = s.intensity * 0.92;
  const ar = s.ambient * 0.99;
  const ag = s.ambient;
  const ab = s.ambient * 1.04;
  const peak = Math.max(lr + ar, lg + ag, lb + ab, 1e-6);
  const norm = peak > 1 ? 1 / peak : 1;
  u.lightColor.value.setRGB(lr * norm, lg * norm, lb * norm);
  u.ambientColor.value.setRGB(ar * norm, ag * norm, ab * norm);
}

const CULL_TO_SIDE: Record<CullMode, THREE.Side> = {
  Off: THREE.DoubleSide,
  Front: THREE.BackSide,
  Back: THREE.FrontSide,
};

export function resolveSide(cull: CullMode): THREE.Side {
  return CULL_TO_SIDE[cull] ?? THREE.FrontSide;
}

function uvVec4(slot: { uvScale?: [number, number]; uvOffset?: [number, number] } | undefined): THREE.Vector4 {
  const s = slot?.uvScale ?? [1, 1];
  const o = slot?.uvOffset ?? [0, 0];
  return new THREE.Vector4(s[0], s[1], o[0], o[1]);
}

// シェイプキー（ブレンドシェイプ）の自前 GPU 実装。
// Three.js 標準の morphtarget チャンクは DataArrayTexture の layer 数 =シェイプキー数
// となり MAX_ARRAY_TEXTURE_LAYERS（保証値 256）で頭打ちになるため使わない。
// ここでは 2D テクスチャの height 方向にシェイプキーを積み、texelFetch で読む。
// 頂点数が MAX_TEXTURE_SIZE（テクスチャ幅の上限）を超える場合は、頂点方向を
// texWidth/rowsPerShape で複数行に折り返す 2D タイル化を行う（geometry.ts 参照）。
// 実質上限はテクスチャ高さ（blendshapeCount*rowsPerShape）が MAX_TEXTURE_SIZE（通常 8192 以上）を
// 超えないこと。
const MORPH_HEAD = /* glsl */ `
#include <common>
#ifdef FM_HAS_MORPH
  uniform highp sampler2D fmShapeDeltaPos;
  uniform highp sampler2D fmShapeDeltaNrm;
  uniform highp sampler2D fmShapeInfluences;
  uniform int fmShapeCount;
  uniform int fmShapeTexWidth;
  uniform int fmShapeRowsPerShape;
#endif
`;

const MORPH_BODY = /* glsl */ `
  vec3 objectNormal = vec3(normal);
  vec3 transformed = vec3(position);
  #ifdef FM_HAS_MORPH
    int fmCol = gl_VertexID % fmShapeTexWidth;
    int fmRow = gl_VertexID / fmShapeTexWidth;
    for (int i = 0; i < fmShapeCount; i++) {
      float inf = texelFetch(fmShapeInfluences, ivec2(i, 0), 0).r;
      if (inf == 0.0) continue;
      ivec2 uvIdx = ivec2(fmCol, fmRow + i * fmShapeRowsPerShape);
      transformed += texelFetch(fmShapeDeltaPos, uvIdx, 0).xyz * inf;
      objectNormal += texelFetch(fmShapeDeltaNrm, uvIdx, 0).xyz * inf;
    }
  #endif
`;

const BASE_VERTEX = /* glsl */ `
attribute vec4 tangent;
${MORPH_HEAD}
varying vec2 vUv;
varying vec3 vNormalV;
varying vec4 vTangentV;
void main() {
${MORPH_BODY}
  vUv = uv;
  vNormalV = normalize(normalMatrix * objectNormal);
  vTangentV = vec4(normalize(normalMatrix * tangent.xyz), tangent.w);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
}
`;

const BASE_FRAGMENT = /* glsl */ `
uniform sampler2D mainTex;
uniform sampler2D shadeTex;
uniform sampler2D normalMap;
uniform vec4 mainColor;
uniform vec4 shadeColor;
uniform float shadeBorder;
uniform float shadeBlur;
uniform float alphaCutoff;
uniform vec4 uvMain;   // scale.xy, offset.zw
uniform vec4 uvShade;
uniform vec4 uvNormal;
uniform vec3 lightDir;     // ワールド、正規化済み、表面→光源
uniform vec3 lightColor;   // Linear
uniform vec3 ambientColor; // Linear
varying vec2 vUv;
varying vec3 vNormalV;
varying vec4 vTangentV;
void main() {
  vec4 baseCol = texture2D(mainTex, vUv * uvMain.xy + uvMain.zw) * mainColor; // Linear
  float alpha = baseCol.a;
  #ifdef FM_ALPHA_CLIP
    if (alpha < alphaCutoff) discard;
  #endif
  #ifndef FM_ALPHA_BLEND
    alpha = 1.0;
  #endif

  // タンジェント空間ノーマルマップ（ビュー空間 TBN、Gram-Schmidt 直交化）
  vec3 nTex = texture2D(normalMap, vUv * uvNormal.xy + uvNormal.zw).xyz * 2.0 - 1.0;
  vec3 N = normalize(vNormalV);
  vec3 T = normalize(vTangentV.xyz - dot(vTangentV.xyz, N) * N);
  vec3 B = cross(N, T) * vTangentV.w;
  vec3 normalV = normalize(T * nTex.x + B * nTex.y + N * nTex.z);
  if (!gl_FrontFacing) normalV = -normalV;

  // トゥーンランプ: dot(N,L) を [0,1] に写し、border/blur でしきい値化
  vec3 L = normalize((viewMatrix * vec4(lightDir, 0.0)).xyz);
  float h = dot(normalV, L) * 0.5 + 0.5;
  float blur = max(shadeBlur, 1e-4);
  float factor = smoothstep(shadeBorder - blur, shadeBorder + blur, h);

  vec3 shadeCol = texture2D(shadeTex, vUv * uvShade.xy + uvShade.zw).rgb * shadeColor.rgb;
  // 色（影色補間）と光量（環境光 + キーライト）を分けて合成し、影側が潰れないようにする
  vec3 albedo = mix(shadeCol, baseCol.rgb, factor);
  vec3 lighting = ambientColor + lightColor * factor;
  gl_FragColor = vec4(albedo * lighting, alpha);
  #include <colorspace_fragment>
}
`;

const OUTLINE_VERTEX = /* glsl */ `
${MORPH_HEAD}
uniform sampler2D outlineMask;
uniform vec4 uvOutlineMask;
uniform float outlineWidth; // メートル
varying vec2 vUv;
varying float vOutlineWidth;
void main() {
${MORPH_BODY}
  float mask = textureLod(outlineMask, uv * uvOutlineMask.xy + uvOutlineMask.zw, 0.0).r;
  float width = outlineWidth * mask;
  vOutlineWidth = width;
  transformed += normalize(objectNormal) * width;
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
}
`;

const OUTLINE_FRAGMENT = /* glsl */ `
uniform sampler2D mainTex;
uniform vec4 mainColor;
uniform vec4 outlineColor;
uniform float alphaCutoff;
uniform vec4 uvMain;
varying vec2 vUv;
varying float vOutlineWidth;
void main() {
  // 幅 0 の縮退ポリゴンが本体と同位置に残り目等を覆う不具合を防ぐ
  if (vOutlineWidth <= 0.000001) discard;
  float alpha = texture2D(mainTex, vUv * uvMain.xy + uvMain.zw).a * mainColor.a;
  #ifdef FM_ALPHA_CLIP
    if (alpha < alphaCutoff) discard;
  #endif
  #ifndef FM_ALPHA_BLEND
    alpha = 1.0;
  #endif
  gl_FragColor = vec4(outlineColor.rgb, outlineColor.a * alpha);
  #include <colorspace_fragment>
}
`;

function applyAlphaMode(mat: THREE.ShaderMaterial, m: FmMaterial): void {
  if (m.alphaMode === "AlphaClip") {
    mat.defines = { ...mat.defines, FM_ALPHA_CLIP: "" };
  } else if (m.alphaMode === "AlphaBlend") {
    mat.defines = { ...mat.defines, FM_ALPHA_BLEND: "" };
    mat.transparent = true;
    // Unity側の _ZWrite をそのまま反映する。ZWrite=On の半透明（lilToonが
    // Cutout寄りの半透明表現で使う設定）を depthWrite=false 固定にすると、
    // 深度テスト・描画順がUnityの実際の見た目と食い違い、瞳などが白っぽく
    // 淡く見える不具合の原因になっていた。
    mat.depthWrite = m.properties.zWrite;
  }
}

/** シェイプキー用 uniform の空テンプレート（実値は viewer.ts が clone 後に割り当てる） */
function shapeUniformTemplate(): Record<string, { value: unknown }> {
  return {
    fmShapeDeltaPos: { value: null },
    fmShapeDeltaNrm: { value: null },
    fmShapeInfluences: { value: null },
    fmShapeCount: { value: 0 },
    fmShapeTexWidth: { value: 1 },
    fmShapeRowsPerShape: { value: 1 },
  };
}

/** メッシュ固有のシェイプデータ。null なら FM_HAS_MORPH を付けずループを省略する。 */
export interface ShapeBinding {
  posTex: THREE.Texture;
  nrmTex: THREE.Texture;
  influencesTex: THREE.Texture;
  count: number;
  texWidth: number;
  rowsPerShape: number;
}

/** fmShape* 以外の uniform はテンプレートと共有する（メッシュ固有なのはシェイプだけ） */
const SHAPE_UNIFORM_KEYS = [
  "fmShapeDeltaPos",
  "fmShapeDeltaNrm",
  "fmShapeInfluences",
  "fmShapeCount",
  "fmShapeTexWidth",
  "fmShapeRowsPerShape",
];

/**
 * テンプレートマテリアルを clone し、メッシュ固有のシェイプ uniform を割り当てる。
 *
 * THREE の clone() は uniform 値をディープコピーする（Texture も clone される）。
 * 非同期デコード中の PNG テクスチャを clone すると image が空のままになり描画が壊れるうえ、
 * ライティング uniform の共有も切れてしまう。そのためシェイプ以外の uniform は
 * テンプレート側のオブジェクト参照へ戻して共有する。
 */
export function cloneWithShape(
  template: THREE.Material,
  shape: ShapeBinding | null,
  light: LightingUniforms,
): THREE.Material {
  if (!(template instanceof THREE.ShaderMaterial)) return template;
  // シェイプキーの無いメッシュはメッシュ固有の状態が無いのでテンプレートをそのまま共有する
  if (!shape || shape.count <= 0) return template;
  const mat = template.clone();
  for (const k of Object.keys(template.uniforms)) {
    if (SHAPE_UNIFORM_KEYS.includes(k)) {
      mat.uniforms[k] = { value: template.uniforms[k].value };
    } else {
      mat.uniforms[k] = template.uniforms[k];
    }
  }
  // ライティングは全マテリアル共有（UI から一括変更するため）
  if (mat.uniforms.lightDir) mat.uniforms.lightDir = light.lightDir;
  if (mat.uniforms.lightColor) mat.uniforms.lightColor = light.lightColor;
  if (mat.uniforms.ambientColor) mat.uniforms.ambientColor = light.ambientColor;
  if (shape && shape.count > 0) {
    mat.defines = { ...mat.defines, FM_HAS_MORPH: "" };
    mat.uniforms.fmShapeDeltaPos.value = shape.posTex;
    mat.uniforms.fmShapeDeltaNrm.value = shape.nrmTex;
    mat.uniforms.fmShapeInfluences.value = shape.influencesTex;
    mat.uniforms.fmShapeCount.value = shape.count;
    mat.uniforms.fmShapeTexWidth.value = shape.texWidth;
    mat.uniforms.fmShapeRowsPerShape.value = shape.rowsPerShape;
  }
  return mat;
}

export interface SlotTextures {
  mainTex: THREE.Texture;
  shadeTex: THREE.Texture;
  normalMap: THREE.Texture;
  outlineMask: THREE.Texture;
}

/** 本体（トゥーン）マテリアル */
export function createLiltoonMaterial(
  m: FmMaterial,
  tex: SlotTextures,
  light: LightingUniforms,
): THREE.ShaderMaterial {
  const p = m.properties;
  const mat = new THREE.ShaderMaterial({
    name: m.name,
    vertexShader: BASE_VERTEX,
    fragmentShader: BASE_FRAGMENT,
    uniforms: {
      mainTex: { value: tex.mainTex },
      shadeTex: { value: tex.shadeTex },
      normalMap: { value: tex.normalMap },
      mainColor: { value: new THREE.Vector4(...p.mainColor) },
      shadeColor: { value: new THREE.Vector4(...p.shadeColor) },
      shadeBorder: { value: p.shadowBorder },
      shadeBlur: { value: p.shadowBlur },
      alphaCutoff: { value: p.alphaCutoff },
      uvMain: { value: uvVec4(m.textures.mainTex) },
      uvShade: { value: uvVec4(m.textures.shadeTex) },
      uvNormal: { value: uvVec4(m.textures.normalMap) },
      lightDir: light.lightDir,
      lightColor: light.lightColor,
      ambientColor: light.ambientColor,
      ...shapeUniformTemplate(),
    },
    side: resolveSide(p.cull),
  });
  // _ColorMask=0（lilToon等が深度マスク専用パスに使う設定）は色を一切書き込まない。
  // 無視すると本来Unity上では不可視のはずのマテリアルがWeb側にだけ見えてしまう。
  mat.colorWrite = p.colorWrite;
  applyAlphaMode(mat, m);
  return mat;
}

/** アウトライン（反転ハル）マテリアル。outlineWidth<=0 なら null */
export function createOutlineMaterial(m: FmMaterial, tex: SlotTextures): THREE.ShaderMaterial | null {
  const p = m.properties;
  if (!(p.outlineWidth > 0)) return null;
  const mat = new THREE.ShaderMaterial({
    name: `${m.name} (outline)`,
    vertexShader: OUTLINE_VERTEX,
    fragmentShader: OUTLINE_FRAGMENT,
    uniforms: {
      mainTex: { value: tex.mainTex },
      outlineMask: { value: tex.outlineMask },
      mainColor: { value: new THREE.Vector4(...p.mainColor) },
      outlineColor: { value: new THREE.Vector4(...p.outlineColor) },
      alphaCutoff: { value: p.alphaCutoff },
      uvMain: { value: uvVec4(m.textures.mainTex) },
      outlineWidth: { value: Math.min(p.outlineWidth * OUTLINE_WIDTH_SCALE, OUTLINE_MAX_METERS) },
      ...shapeUniformTemplate(),
    },
    side: THREE.BackSide,
  });
  applyAlphaMode(mat, m);
  return mat;
}
